const ACCESS_KEY = 'access_token';
const REFRESH_KEY = 'refresh_token';
const ACTIVITY_KEY = 'ar_last_activity_at';
const CACHE_KEY = 'ar_authenticated_user_cache';
const IDLE_MS = 8 * 60 * 60 * 1000;

// Used only to preserve a mounted form during rotation. Server authorization
// still verifies the signed token and current permissions on every request.
export function sameAdministrativeSession(previousToken, nextToken) {
  try {
    const decode = token => {
      const encoded = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
      return JSON.parse(atob(encoded));
    };
    const previous = decode(previousToken), next = decode(nextToken);
    return Boolean(previous.sub && previous.sid && previous.sub === next.sub && previous.sid === next.sid);
  } catch { return false; }
}

export class AuthSessionChangedError extends Error {
  constructor() { super('La sesión cambió. Vuelve a abrir la operación.'); this.name = 'AuthSessionChangedError'; }
}

// All administrative token writes share one owner. An old response cannot
// restore tokens after logout, a new login or a change in another tab.
export function createAuthSessionCoordinator({ storage, post, onTokens, onClear, now = Date.now }) {
  let revision = 0;
  let refreshFlight = null;
  let locallyClosed = false;
  const pair = () => ({ access_token: storage.get(ACCESS_KEY), refresh_token: storage.get(REFRESH_KEY) });
  const invalidate = () => { revision += 1; refreshFlight = null; return revision; };
  let observedPair = pair();
  const synchronize = () => {
    const current = pair();
    if (current.access_token !== observedPair.access_token || current.refresh_token !== observedPair.refresh_token) {
      invalidate(); observedPair = current;
      locallyClosed = !current.access_token || !current.refresh_token;
    }
    return current;
  };
  const isCurrent = (value) => { synchronize(); return value === revision; };
  const clear = () => {
    const next = invalidate();
    locallyClosed = true;
    [ACCESS_KEY, REFRESH_KEY, ACTIVITY_KEY, CACHE_KEY].forEach((key) => { try { storage.remove(key); } catch {} });
    observedPair = pair();
    onClear();
    return next;
  };
  const acceptPair = (tokens) => {
    if (typeof tokens?.access_token !== 'string' || !tokens.access_token
      || typeof tokens?.refresh_token !== 'string' || !tokens.refresh_token) {
      throw new Error('La respuesta de sesión está incompleta. Inicia sesión nuevamente.');
    }
    try {
      storage.set(ACCESS_KEY, tokens.access_token);
      storage.set(REFRESH_KEY, tokens.refresh_token);
    } catch (error) { clear(); throw error; }
    observedPair = pair();
    locallyClosed = false;
    onTokens(tokens.access_token);
  };
  const refresh = async () => {
    synchronize();
    if (locallyClosed) return null;
    const activity = Number(storage.get(ACTIVITY_KEY) || 0);
    if (activity && now() - activity >= IDLE_MS) { clear(); return null; }
    const initial = pair();
    if (!initial.refresh_token) return null;
    if (refreshFlight && refreshFlight.revision === revision && refreshFlight.token === initial.refresh_token) return refreshFlight.promise;
    const operation = { revision, token: initial.refresh_token };
    operation.promise = Promise.resolve().then(() => {
      if (!isCurrent(operation.revision) || storage.get(REFRESH_KEY) !== initial.refresh_token) return null;
      return post('/auth/refresh', { refresh_token: initial.refresh_token });
    })
      .then((response) => {
        if (!isCurrent(operation.revision) || storage.get(REFRESH_KEY) !== initial.refresh_token) return null;
        acceptPair(response.data);
        return response.data;
      }).finally(() => { if (refreshFlight === operation) refreshFlight = null; });
    refreshFlight = operation;
    return operation.promise;
  };
  const logout = async () => {
    const tokens = pair();
    const closedRevision = clear();
    if (!tokens.access_token && !tokens.refresh_token) return { closedRevision, data: { full_session_revoked: true } };
    try {
      const response = await post('/auth/logout', tokens.refresh_token ? { refresh_token: tokens.refresh_token } : {},
        { headers: tokens.access_token ? { Authorization: `Bearer ${tokens.access_token}` } : {} });
      return { closedRevision, data: response.data };
    } catch (error) {
      error.closedRevision = closedRevision;
      throw error;
    }
  };
  return {
    pair, refresh, logout, clear, isCurrent,
    revision: () => { synchronize(); return revision; },
    invalidatePending: invalidate,
    beginLogin: clear,
    acceptLogin(tokens, expectedRevision) {
      if (!isCurrent(expectedRevision)) return false;
      acceptPair(tokens);
      storage.set(ACTIVITY_KEY, String(now()));
      return true;
    },
    externalChange() {
      return synchronize();
    },
    clearIfCurrent(expectedRevision) { if (isCurrent(expectedRevision)) clear(); },
  };
}
